import {
    Alert,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
} from "@mui/material";
import { useFetch, useSnackbar } from "../../../hooks";
import { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { AddTraitGroupDialog } from "./AddTraitGroupDialog";
import { BACKEND_ROUTES } from "../../../backendRoutes";
import { FRONTEND_ROUTES } from "../../../frontendRoutes";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";
import { TRAIT_GROUP_ASSOCIABLE_TYPES } from "../../../constants";
import { TraitGroup } from "./TraitGroup";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export const TraitGroupList = ({
    experimentMissionTraitGroups,
    pipelines,
    experimentMission,
    successMutate,
    hideNoPipelines,
    parentPipelineInstance,
    instantiateAll,
    compatibleExperimentMissions,
}) => {
    const { post } = useFetch();
    const { openSnackbar } = useSnackbar();
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const navigate = useNavigate();

    const {
        handleSubmit,
        formState: { errors },
        setError,
    } = useFormContext();

    const sortedExperimentMissionTraitGroups = useMemo(
        () =>
            experimentMissionTraitGroups.toSorted((a, b) =>
                a.TraitGroupDataType.TraitGroup.name.localeCompare(
                    b.TraitGroupDataType.TraitGroup.name
                )
            ),
        [experimentMissionTraitGroups]
    );

    const filteredTraitGroups = useMemo(() => {
        if (!hideNoPipelines) return sortedExperimentMissionTraitGroups;

        return sortedExperimentMissionTraitGroups.filter(
            ({ TraitGroupDataType }) =>
                pipelines.some(
                    ({ traitGroupDataTypeUuid }) =>
                        traitGroupDataTypeUuid === TraitGroupDataType.uuid
                )
        );
    }, [sortedExperimentMissionTraitGroups, hideNoPipelines, pipelines]);

    const onSubmit = async (payload) => {
        const checkedTraitGroups = Object.entries(payload.traitGroups).filter(
            ([, { checked }]) => checked
        );

        if (checkedTraitGroups.length) {
            // Build the list of associated trait groups from the current experiment mission
            const associatedTraitGroups = checkedTraitGroups.map(
                ([key, { mode }]) => ({
                    uuid: key,
                    mode,
                })
            );

            // When instantiateAll is checked, also include matching associated trait groups
            // from compatible experiment missions
            if (instantiateAll && compatibleExperimentMissions.length) {
                // Get the TraitGroupDataType UUIDs of checked trait groups
                checkedTraitGroups.forEach(([uuid, { mode }]) => {
                    const EMTG = experimentMissionTraitGroups.find(
                        (row) => row.uuid === uuid
                    );
                    for (const cem of compatibleExperimentMissions) {
                        if (!cem.AssociatedTraitGroups) continue;
                        for (const atg of cem.AssociatedTraitGroups) {
                            if (
                                EMTG.traitGroupDataTypeUuid ===
                                atg.traitGroupDataTypeUuid
                            ) {
                                associatedTraitGroups.push({
                                    uuid: atg.uuid,
                                    mode: mode,
                                });
                            }
                        }
                    }
                });
            }

            const body = {
                associatedTraitGroupType:
                    TRAIT_GROUP_ASSOCIABLE_TYPES.EXPERIMENT_MISSION,
                associatedTraitGroups,
            };

            const pipelineInstance = await post(
                BACKEND_ROUTES.PIPELINE_INSTANCE,
                { body }
            );

            // Create then translate
            if (pipelineInstance) {
                if (parentPipelineInstance) {
                    try {
                        await post(
                            `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}/translate`,
                            {
                                body: {
                                    parentPipelineInstanceUuid:
                                        parentPipelineInstance,
                                },
                                forwardError: true,
                            }
                        );
                    } catch (error) {
                        openSnackbar(
                            `Translation failed: ${error.message}`,
                            "error"
                        );
                        return; // Don't navigate if translation failed
                    }
                }

                openSnackbar(
                    "Pipeline instance created and translated",
                    "success"
                );
                navigate(
                    `/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstance.uuid}`
                );
            }
        } else {
            setError("root.traitGroups", {
                message: "At least one trait group must be selected",
            });
        }
    };

    return (
        <>
            {errors.root?.traitGroups?.message && (
                <Alert severity="error">
                    {errors.root.traitGroups.message}
                </Alert>
            )}
            <List
                component="form"
                id="traitGroups"
                onSubmit={handleSubmit(onSubmit)}
            >
                {filteredTraitGroups.map(
                    ({
                        TraitGroupDataType,
                        uuid,
                        PipelineInstances,
                        ValidatedPipelineInstance,
                    }) => (
                        <TraitGroup
                            key={uuid}
                            fieldArrayName={`traitGroups.${uuid}`}
                            name={TraitGroupDataType.TraitGroup.name}
                            dataType={TraitGroupDataType.dataType}
                            pipelines={pipelines.filter(
                                ({ traitGroupDataTypeUuid }) =>
                                    traitGroupDataTypeUuid ===
                                    TraitGroupDataType.uuid
                            )}
                            pipelineInstances={PipelineInstances}
                            validatedPipelineInstance={
                                ValidatedPipelineInstance
                            }
                            uuid={uuid}
                            traitGroupDataTypeUuid={TraitGroupDataType.uuid}
                        />
                    )
                )}
                <ListItem disableGutters sx={{ width: "30%" }}>
                    <ListItemButton
                        sx={{ borderRadius: 1 }}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                                <AddIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="Add trait group" />
                    </ListItemButton>
                </ListItem>
            </List>
            <AddTraitGroupDialog
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                experimentMission={experimentMission}
                experimentMissionTraitGroups={experimentMissionTraitGroups}
                onSuccess={successMutate}
            />
        </>
    );
};

TraitGroupList.propTypes = {
    experimentMissionTraitGroups: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.ExperimentMissionTraitGroup).isRequired
    ).isRequired,
    pipelines: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.Pipeline).isRequired
    ).isRequired,
    experimentMission: PropTypes.shape(MODEL_PROPTYPES.ExperimentMission)
        .isRequired,
    successMutate: PropTypes.func.isRequired,
    hideNoPipelines: PropTypes.bool.isRequired,
    parentPipelineInstance: PropTypes.string.isRequired,
    instantiateAll: PropTypes.bool.isRequired,
    compatibleExperimentMissions: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.ExperimentMission)
    ).isRequired,
};
